"use client";

import { useId, useRef, useState } from "react";
import { Check, MoveRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HoneypotField } from "@/components/marketing/honeypot-field";
import { HONEYPOT_FIELD, ELAPSED_FIELD } from "@/lib/form-fields";
import { cn } from "@/lib/utils";

type Status = "idle" | "loading" | "success" | "error";

interface NewsletterFormProps {
  /** Tracking label for where the signup came from. */
  source?: string;
  /** "dark" for dark/feature backgrounds, "light" for parchment surfaces. */
  tone?: "dark" | "light";
  /** Stack the field and button vertically instead of inline. */
  stacked?: boolean;
  buttonLabel?: string;
  className?: string;
}

/**
 * Reusable, accessible newsletter signup. Submits to `/api/subscribe` via fetch
 * with inline success/error states (no full-page navigation). Used by the
 * footer and marketing CTAs so every email capture behaves consistently.
 */
export function NewsletterForm({
  source = "site",
  tone = "light",
  stacked = false,
  buttonLabel = "Subscribe",
  className,
}: NewsletterFormProps) {
  const id = useId();
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const startedAt = useRef(Date.now());

  const dark = tone === "dark";

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const email = String(formData.get("email") ?? "").trim();

    setStatus("loading");
    setMessage("");

    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          source,
          [HONEYPOT_FIELD]: String(formData.get(HONEYPOT_FIELD) ?? ""),
          [ELAPSED_FIELD]: Date.now() - startedAt.current,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };
      if (!res.ok) {
        throw new Error(data.error ?? "Something went wrong. Please try again.");
      }
      setStatus("success");
      setMessage(data.message ?? "You're in. Check your inbox.");
      form.reset();
    } catch (err) {
      setStatus("error");
      setMessage(
        err instanceof Error ? err.message : "Something went wrong. Please try again.",
      );
    }
  }

  if (status === "success") {
    return (
      <p
        role="status"
        className={cn(
          "inline-flex items-center gap-2 text-sm font-medium",
          dark ? "text-white" : "text-primary",
          className,
        )}
      >
        <Check className="size-4 shrink-0 text-secondary" aria-hidden="true" />
        {message}
      </p>
    );
  }

  return (
    <div className={className}>
      <form
        onSubmit={handleSubmit}
        className={cn("flex gap-2", stacked ? "flex-col" : "flex-col sm:flex-row")}
        noValidate
      >
        <HoneypotField />
        <label htmlFor={`${id}-email`} className="sr-only">
          Email address
        </label>
        <input
          id={`${id}-email`}
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder="you@example.co.uk"
          aria-invalid={status === "error" ? true : undefined}
          aria-describedby={status === "error" ? `${id}-msg` : undefined}
          className={cn(
            "min-h-[48px] min-w-0 flex-1 rounded-md px-4 text-base transition-colors",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
            dark
              ? "border border-white/20 bg-white/10 text-white placeholder:text-white/50"
              : "border border-line bg-paper text-dark placeholder:text-mid/60 hover:border-mid/50",
          )}
        />
        <Button
          type="submit"
          variant="secondary"
          size="lg"
          className="shrink-0"
          disabled={status === "loading"}
        >
          {status === "loading" ? "Subscribing…" : buttonLabel}
          {status !== "loading" && <MoveRight className="size-4" aria-hidden="true" />}
        </Button>
      </form>
      {status === "error" && (
        <p
          id={`${id}-msg`}
          role="alert"
          className={cn("mt-2 text-sm", dark ? "text-amber-200" : "text-error")}
        >
          {message}
        </p>
      )}
    </div>
  );
}
